import {
    useGetAccounts,
    useGetFormalOffers,
    useGetIwocapayCapturedPayments,
    useGetIwocapayPaymentLater
} from '@api/lendingAPI/lapiHooks';
import {
    getTotalOutstanding,
    getTotalScheduledPayments
} from '../Dashboard.helpers';
import { getLoanDurationInMonths } from '../../../myOffers/MyOffers.helpers';
import { GetAccountsResponse } from '@iwoca/lapi-client/edge';
import { SpendingLimits } from '../Dashboard.types';
import { mapDurationToSellerProduct } from '../OfferChoiceForm/utils/mapDurationToSellerProduct';
import { FormalOffer } from '../../../../myAccount/myOffers/MyOffers.types';

const useGetIwocaPayAccounts = () => {
    const { data: accounts, isLoading: loadingAccounts } = useGetAccounts();

    return {
        accounts: accounts?.accounts
            .filter((account) => account.product_type === 'iwocapay')
            .filter((account) => !!account.account_id), // active only
        loadingAccounts
    };
};

const useGetIwocapayFormalOffers = () => {
    const { formalOffers, loadingFormalOffers } = useGetFormalOffers();

    return {
        formalOffers: formalOffers.filter(
            (offer) => offer.product_type === 'iwocapay'
        ),
        loadingFormalOffers
    };
};

export const useGetSpendingLimits = () => {
    const { accounts, loadingAccounts } = useGetIwocaPayAccounts();
    const { buyerPayLinks, loadingBuyerPayLinks } =
        useGetIwocapayPaymentLater();
    const { capturedPayments, loadingCapturedPayments } =
        useGetIwocapayCapturedPayments();

    const { formalOffers } = useGetIwocapayFormalOffers();

    const isLoading =
        loadingAccounts || loadingBuyerPayLinks || loadingCapturedPayments;

    if (
        !accounts ||
        buyerPayLinks === undefined ||
        capturedPayments === undefined ||
        formalOffers.length === 0
    )
        return {
            activeSpendingLimit: null,
            spendingLimits: null,
            loadingSpendingLimits: isLoading
        };

    const totalScheduledAmount = getTotalScheduledPayments(
        buyerPayLinks,
        capturedPayments
    );

    const totalOutstanding =
        getTotalOutstanding(accounts) + totalScheduledAmount;

    const spendingLimits = {
        limits: {
            THREE_MONTHS: getSpendingLimitDict({
                formalOffers: formalOffers,
                totalScheduledAmount,
                duration: 3
            }),
            TWELVE_MONTHS: getSpendingLimitDict({
                formalOffers: formalOffers,
                totalScheduledAmount,
                duration: 12
            })
        },
        totalOutstanding: totalOutstanding,
        isValid: formalOffers[0].is_valid || false,
        expiresIn: getExpiresInDays(formalOffers[0].timeout!)
    };

    const activeSpendingLimit = getActiveSpendingLimit({
        accounts,
        spendingLimits
    });

    return {
        activeSpendingLimit,
        spendingLimits,
        loadingSpendingLimits: isLoading
    };
};

const getActiveSpendingLimit = ({
    accounts,
    spendingLimits
}: {
    accounts: NonNullable<GetAccountsResponse['data']>['accounts'];
    spendingLimits: SpendingLimits;
}) => {
    const activeAccount = accounts ? accounts[0] : null;
    const activeAccountDuration = mapDurationToSellerProduct({
        duration: activeAccount?.duration
    });

    const activeSpendingLimit = activeAccountDuration
        ? spendingLimits.limits[activeAccountDuration]
        : null;

    if (activeSpendingLimit) return activeSpendingLimit;

    // default to 3, then 12, if no active spending limit (we expect some people to only have a 12 for now)
    return (
        spendingLimits.limits.THREE_MONTHS ||
        spendingLimits.limits.TWELVE_MONTHS
    );
};

const getExpiresInDays = (timeout: string) => {
    const day = 1000 * 60 * 60 * 24; // one day in ms
    const timeoutDate = Date.parse(timeout);
    const todaysDate = Date.now();
    return Math.ceil((timeoutDate - todaysDate) / day);
};

const getSpendingLimitDict = ({
    formalOffers,
    totalScheduledAmount,
    duration
}: {
    formalOffers: FormalOffer[];
    totalScheduledAmount: number;
    duration: 3 | 12;
}) => {
    const formalOffer = formalOffers.find(
        (offer) =>
            offer.duration &&
            getLoanDurationInMonths(offer.duration) === duration
    );

    if (!formalOffer) return null;
    return {
        creditLimit: formalOffer.available_cash! - totalScheduledAmount,
        maxCreditLimit: formalOffer.max_amount!,
        interestRate: formalOffer.interest_rates![0].rate!,
        durationInMonths: duration
    };
};
